<template>
  <!-- One conversation in the Workspace369 inbox (lib/view/inbox/inbox_thread_screen.dart and
       public/home/screens/inbox-*.png): the contact header with Search / Pinned messages / Contact / Call /
       Status / More, the quick actions (Send Invoice, Book Appointment, Request Review, Send
       Payment Link), the SMS divider, the message menu (Pin, Add to notes, Create task, Translate) and
       the composer with its channel switcher (SMS, Email, Note in the app's channel colours,
       widgets/composer/channel_switcher.dart) and the hint "Write a message... (type / for quick
       replies)". The client and messages are sample data.
       Five screens share one fixed box and cross-fade on `state`:
       arrive → context → assign → reply → task. The server renders `task`, the last step. -->
  <div class="itw" :class="`itw--${state}`" aria-hidden="true">
    <header class="itw-head">
      <div class="itw-who">
        <span><b>Maya Ortiz</b><small>+1 (512) 555-0182</small></span>
        <span class="itw-tagbtn"><svg viewBox="0 0 24 24"><path :d="icons.plus" /></svg>Tag</span>
      </div>
      <div class="itw-btns">
        <span v-for="b in headButtons" :key="b.label" class="itw-btn" :class="{ 'is-hot': b.hot }"><svg viewBox="0 0 24 24"><path :d="icons[b.icon]" /></svg>{{ b.label }}</span>
      </div>
      <div class="itw-btns">
        <span v-for="b in quickActions" :key="b.label" class="itw-btn"><svg viewBox="0 0 24 24"><path :d="icons[b.icon]" /></svg>{{ b.label }}</span>
      </div>
    </header>

    <div class="itw-body">
      <p class="itw-divider"><svg viewBox="0 0 24 24"><path :d="icons.sms" /></svg>SMS</p>
      <div class="itw-msg itw-msg--in">
        <span class="itw-av">MO</span>
        <div>
          <small>Maya Ortiz</small>
          <p>Hi! Can we move the shoot to Thursday the 29th? Is the studio still free?</p>
          <time>9:41</time>
        </div>
      </div>

      <div class="itw-note itw-late">
        <span><svg viewBox="0 0 24 24"><path :d="icons.note" /></svg>Internal</span>
        <p>Sam, can you check the studio for Thursday the 29th?</p>
      </div>

      <div class="itw-msg itw-msg--out itw-late2">
        <p>Thursday works! See you Oct 29 at 10:00 at the studio.</p>
        <time>9:46 ✓✓</time>
      </div>

      <!-- context: the client panel beside the conversation -->
      <aside class="itw-panel itw-pop itw-pop--context">
        <span class="itw-panel-av">MO</span>
        <b>Maya Ortiz</b>
        <span class="itw-panel-acts">
          <i><svg viewBox="0 0 24 24"><path :d="icons.phone" /></svg></i>
          <i><svg viewBox="0 0 24 24"><path :d="icons.sms" /></svg></i>
          <i><svg viewBox="0 0 24 24"><path :d="icons.mail" /></svg></i>
        </span>
        <dl>
          <div><dt>Company</dt><dd>Ortiz Studio</dd></div>
          <div><dt>Lead status</dt><dd>Client</dd></div>
          <div><dt>Phone</dt><dd>+15125550182</dd></div>
          <div><dt>Email</dt><dd>maya@ortiz.test</dd></div>
        </dl>
        <small class="itw-panel-link">View full client profile →</small>
      </aside>

      <!-- assign: the assignment picker -->
      <div class="itw-menu itw-pop itw-pop--assign">
        <b class="itw-menu-title">Assign to teammate</b>
        <span class="is-on"><i>SR</i>Sam Rivera<svg viewBox="0 0 24 24"><path :d="icons.check" /></svg></span>
        <span><i>PS</i>Priya Shah</span>
        <span class="itw-menu-quiet">Clear assignee</span>
      </div>

      <!-- reply: schedule the send -->
      <div class="itw-tip itw-pop itw-pop--reply">
        <b>Schedule send</b>
        <span>Tomorrow morning · 9:00 AM</span>
      </div>

      <!-- task: the message menu -->
      <div class="itw-menu itw-menu--msg itw-pop itw-pop--task">
        <span><svg viewBox="0 0 24 24"><path :d="icons.pin" /></svg>Pin</span>
        <span><svg viewBox="0 0 24 24"><path :d="icons.noteAdd" /></svg>Add to notes</span>
        <span class="is-on"><svg viewBox="0 0 24 24"><path :d="icons.checkCircle" /></svg>Create task</span>
        <span><svg viewBox="0 0 24 24"><path :d="icons.translate" /></svg>Translate</span>
      </div>
      <p class="itw-toast itw-pop itw-pop--task"><svg viewBox="0 0 24 24"><path :d="icons.check" /></svg>Task created</p>
    </div>

    <div class="itw-composer">
      <span class="itw-channel"><svg viewBox="0 0 24 24"><path :d="icons.sms" /></svg><b class="itw-ch-sms">SMS</b><b class="itw-ch-note">Note</b><svg class="itw-caret" viewBox="0 0 24 24"><path :d="icons.caret" /></svg></span>
      <div class="itw-field">
        <p class="itw-hint">Write a message... (type / for quick replies)</p>
        <p class="itw-hint itw-hint--note">Write an internal note...</p>
        <p class="itw-typed">Thursday works! See you Oct 29 at 10:00 at the studio.</p>
        <div class="itw-tools">
          <svg viewBox="0 0 24 24"><path :d="icons.sparkle" /></svg>
          <svg viewBox="0 0 24 24"><path :d="icons.clip" /></svg>
          <svg viewBox="0 0 24 24"><path :d="icons.emoji" /></svg>
          <span class="itw-clock"><svg viewBox="0 0 24 24"><path :d="icons.clock" /></svg></span>
          <span class="itw-send"><svg viewBox="0 0 24 24"><path :d="icons.send" /></svg></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  // arrive | context | assign | reply | task
  state: { type: String, default: "task" },
});

const headButtons = [
  { label: "Search", icon: "search" },
  { label: "Pinned messages", icon: "pin" },
  { label: "Contact", icon: "person", hot: true },
  { label: "Call", icon: "phone" },
  { label: "Status", icon: "checkCircle" },
  { label: "More", icon: "more" },
];
const quickActions = [
  { label: "Send Invoice", icon: "receipt" },
  { label: "Book Appointment", icon: "calendar" },
  { label: "Request Review", icon: "star" },
  { label: "Send Payment Link", icon: "card" },
];
const icons = {
  plus: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
  search: "M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  pin: "M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z",
  person: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  checkCircle: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",
  more: "M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z",
  receipt: "M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z",
  calendar: "M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z",
  star: "m22 9.24-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z",
  card: "M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z",
  sms: "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z",
  mail: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z",
  note: "M19 3H4.99C3.89 3 3 3.9 3 5l.01 14c0 1.1.89 2 1.99 2h10l6-6V5c0-1.1-.9-2-2-2zM7 8h10v2H7V8zm5 6H7v-2h5v2zm2 5.5V14h5.5L14 19.5z",
  noteAdd: "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 14h-3v3h-2v-3H8v-2h3v-3h2v3h3v2zm-3-7V3.5L18.5 9H13z",
  translate: "m12.87 15.07-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7 1.62-4.33L19.12 17h-3.24z",
  check: "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  caret: "M7 10l5 5 5-5z",
  sparkle: "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z",
  clip: "M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z",
  emoji: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z",
  clock: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
  send: "M2.01 21 23 12 2.01 3 2 10l15 2-15 2z",
};
</script>

<style>
.itw {
  --itw-page: #fff;
  --itw-wash: #f4f6f8;
  --itw-card: #fff;
  --itw-line: rgb(0 0 0 / 0.1);
  --itw-text: #171717;
  --itw-muted: #5f6368;
  --itw-ink: #0f172a;
  --itw-ink-text: #fff;
  --itw-bubble: #f1f3f5;
  --itw-sms: #1e40af;
  --itw-note: #a16207;
  --itw-note-fill: #fef9e7;
  --itw-hot: #e0f2f7;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--itw-line);
  border-radius: 10px;
  background: var(--itw-page);
  color: var(--itw-text);
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28), 0 2px 6px rgb(15 23 42 / 0.06);
}
.dark .itw {
  --itw-page: #1c1c1c;
  --itw-wash: #242424;
  --itw-card: #262626;
  --itw-line: rgb(255 255 255 / 0.13);
  --itw-text: #ececec;
  --itw-muted: #a8a8a8;
  --itw-ink: #f8fafc;
  --itw-ink-text: #0f172a;
  --itw-bubble: #2a2a2a;
  --itw-sms: #93aef9;
  --itw-note: #f1c95e;
  --itw-note-fill: rgb(202 138 4 / 0.14);
  --itw-hot: rgb(27 167 206 / 0.18);
  box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7);
}
.itw svg { width: 14px; height: 14px; flex: none; fill: currentColor; }

.itw-head { flex: none; display: grid; gap: 6px; border-bottom: 1px solid var(--itw-line); padding: 10px 12px 8px; }
.itw-who { display: flex; justify-content: space-between; gap: 8px; }
.itw-who > span:first-child { display: grid; line-height: 1.25; }
.itw-who b { font-size: 14px; font-weight: 600; }
.itw-who small { color: var(--itw-muted); font-size: 11px; }
.itw-tagbtn { display: inline-flex; align-items: center; gap: 3px; align-self: start; border: 1px solid var(--itw-line); border-radius: 6px; padding: 2px 7px; color: var(--itw-muted); font-size: 11px; }
.itw-tagbtn svg { width: 12px; height: 12px; }
.itw-btns { display: flex; gap: 5px; overflow: hidden; white-space: nowrap; }
.itw-btn { display: inline-flex; flex: none; align-items: center; gap: 4px; border: 1px solid var(--itw-line); border-radius: 6px; padding: 3px 7px; font-size: 11.5px; }
.itw-btn svg { width: 12px; height: 12px; }

.itw-body { position: relative; display: flex; flex: 1; flex-direction: column; gap: 9px; min-height: 0; overflow: hidden; background: var(--itw-wash); padding: 8px 12px; }
.itw-divider { display: flex; align-items: center; justify-content: center; gap: 5px; margin: 0; color: var(--itw-sms); font-size: 11px; }
.itw-divider svg { width: 12px; height: 12px; }
.itw-msg { display: flex; gap: 7px; }
.itw-msg p { margin: 0; border-radius: 10px; padding: 7px 10px; }
.itw-msg time { display: block; margin-top: 2px; color: var(--itw-muted); font-size: 10.5px; }
.itw-msg small { color: var(--itw-muted); font-size: 11px; }
.itw-av { display: grid; place-items: center; flex: none; align-self: end; width: 24px; height: 24px; margin-bottom: 16px; border-radius: 6px; background: #e0e7ff; color: #3f58bf; font-size: 10px; font-weight: 700; }
.dark .itw-av { background: rgb(91 127 234 / 0.25); color: #b9c8f8; }
.itw-msg--in p { background: var(--itw-card); border: 1px solid var(--itw-line); }
.itw-msg--out { flex-direction: column; align-items: flex-end; }
.itw-msg--out p { max-width: 85%; background: var(--itw-ink); color: var(--itw-ink-text); }
.itw-note { border: 1px solid color-mix(in srgb, var(--itw-note) 35%, transparent); border-radius: 8px; background: var(--itw-note-fill); padding: 6px 10px; }
.itw-note span { display: inline-flex; align-items: center; gap: 4px; color: var(--itw-note); font-size: 10.5px; font-weight: 600; }
.itw-note span svg { width: 12px; height: 12px; }
.itw-note p { margin: 2px 0 0; font-size: 12.5px; }

/* Overlays: each belongs to one step. */
.itw-pop { position: absolute; z-index: 2; border: 1px solid var(--itw-line); border-radius: 10px; background: var(--itw-card); box-shadow: 0 12px 30px -12px rgb(15 23 42 / 0.35); }
.itw-panel { top: 8px; right: 8px; bottom: 8px; display: grid; align-content: start; justify-items: center; gap: 6px; width: min(58%, 230px); padding: 12px; }
.itw-panel-av { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 10px; background: #e0e7ff; color: #3f58bf; font-weight: 700; }
.dark .itw-panel-av { background: rgb(91 127 234 / 0.25); color: #b9c8f8; }
.itw-panel-acts { display: flex; gap: 6px; }
.itw-panel-acts i { display: grid; place-items: center; width: 26px; height: 26px; border: 1px solid var(--itw-line); border-radius: 7px; }
.itw-panel-acts svg { width: 13px; height: 13px; }
.itw-panel dl { display: grid; gap: 4px; width: 100%; margin: 4px 0 0; font-size: 11.5px; }
.itw-panel dl div { display: flex; justify-content: space-between; gap: 6px; }
.itw-panel dt { color: var(--itw-muted); }
.itw-panel dd { overflow: hidden; margin: 0; text-overflow: ellipsis; white-space: nowrap; }
.itw-panel-link { margin-top: 4px; font-size: 11px; }
.itw-menu { display: grid; gap: 1px; padding: 6px; font-size: 12px; }
.itw-menu span { display: flex; align-items: center; gap: 7px; border-radius: 6px; padding: 5px 8px; }
.itw-menu span.is-on { background: var(--itw-hot); font-weight: 600; }
.itw-menu span i { display: grid; place-items: center; width: 20px; height: 20px; border-radius: 5px; background: var(--itw-wash); font-size: 9px; font-style: normal; font-weight: 700; }
.itw-menu span svg:last-child:not(:first-child) { margin-left: auto; }
.itw-menu-title { padding: 4px 8px 2px; color: var(--itw-muted); font-size: 11px; font-weight: 600; }
.itw-menu-quiet { color: var(--itw-muted); }
.itw-pop--assign { top: 8px; right: 12px; width: 190px; }
.itw-pop--task.itw-menu { top: 36px; right: 12px; width: 160px; }
.itw-tip { right: 12px; bottom: 8px; display: grid; padding: 7px 10px; font-size: 11.5px; }
.itw-tip span { color: var(--itw-muted); }
.itw-toast { left: 12px; bottom: 8px; display: flex; align-items: center; gap: 5px; margin: 0; padding: 6px 10px; font-size: 12px; font-weight: 600; }
.itw-toast svg { color: #2d7a4e; }
.dark .itw-toast svg { color: #6ed39a; }

.itw-composer { flex: none; display: grid; gap: 6px; border-top: 1px solid var(--itw-line); padding: 8px 12px 10px; }
.itw-channel { display: inline-flex; align-items: center; gap: 4px; justify-self: start; border-radius: 6px; background: color-mix(in srgb, var(--itw-sms) 12%, transparent); padding: 3px 7px; color: var(--itw-sms); font-size: 11.5px; }
.itw-channel b { font-weight: 600; }
.itw-ch-note { display: none; }
.itw-caret { width: 12px !important; height: 12px !important; }
.itw-field { position: relative; border: 1px solid var(--itw-line); border-radius: 10px; background: var(--itw-card); padding: 8px 10px; }
.itw-hint, .itw-typed { margin: 0; min-height: 18px; font-size: 12.5px; }
.itw-hint { color: var(--itw-muted); }
.itw-hint--note { display: none; color: var(--itw-note); }
.itw-typed { display: none; }
.itw-tools { display: flex; align-items: center; gap: 12px; margin-top: 6px; color: var(--itw-muted); }
.itw-clock { display: grid; place-items: center; width: 22px; height: 22px; margin: -4px; border-radius: 6px; }
.itw-send { display: grid; place-items: center; width: 26px; height: 26px; margin-left: auto; border-radius: 7px; background: var(--itw-wash); }
.itw-send svg { width: 13px; height: 13px; }

/* Which pieces each step shows. Every piece fades, nothing resizes. */
.itw-late, .itw-late2, .itw-pop { opacity: 0; visibility: hidden; transform: translateY(6px); transition: opacity 0.35s ease, transform 0.45s cubic-bezier(0.33, 1, 0.68, 1), visibility 0s linear 0.35s; }
.itw--assign .itw-late, .itw--reply .itw-late, .itw--task .itw-late,
.itw--task .itw-late2,
.itw--context .itw-pop--context, .itw--assign .itw-pop--assign, .itw--reply .itw-pop--reply, .itw--task .itw-pop--task {
  opacity: 1;
  visibility: visible;
  transform: none;
  transition: opacity 0.4s ease 0.1s, transform 0.5s cubic-bezier(0.33, 1, 0.68, 1) 0.1s, visibility 0s;
}
.itw--task .itw-toast { transition-delay: 0.6s; }
.itw--arrive .itw-msg--in { animation: itw-in 0.5s cubic-bezier(0.33, 1, 0.68, 1) 0.2s both; }
.itw--assign .itw-channel { background: color-mix(in srgb, var(--itw-note) 14%, transparent); color: var(--itw-note); }
.itw--assign .itw-ch-sms, .itw--assign .itw-hint:not(.itw-hint--note) { display: none; }
.itw--assign .itw-ch-note, .itw--assign .itw-hint--note { display: block; }
.itw--reply .itw-hint { display: none; }
.itw--reply .itw-typed { display: block; }
.itw--reply .itw-clock { background: var(--itw-hot); color: var(--itw-text); }
.itw--context .itw-btn.is-hot { background: var(--itw-hot); }
@keyframes itw-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .itw *, .itw *::before { animation: none !important; transition: none !important; }
}
</style>
